/**
 * Built-in effect `chromatic` (W2-01b; P§5.2): chromatic aberration radiating from the centre of the screen.
 * Reimplemented from the behaviour (badged approximate).
 *
 * Red is read further out along the line from the centre and blue further in, by `power` times the distance to the
 * centre; green stays put. `sampleCount` (1..64) spreads each channel over that many steps between the pixel and the
 * full offset, so the fringes are smeared instead of doubled. 1 sample = a clean shift.
 */

import { m } from './builtinFx.messages';
import { defineBuiltin, SAMPLE_SCREEN } from './shared';

export const chromatic = defineBuiltin({
  name: 'chromatic',
  label: { key: m.chromatic },
  description: { key: m.chromaticDesc },
  inputs: [],
  uniforms: [
    {
      name: 'sampleCount',
      type: 'float',
      default: 3,
      hint: 'int 1..64',
      min: 1,
      max: 64,
      integer: true,
      label: { key: m.chromaticSampleCount },
    },
    { name: 'power', type: 'float', default: 0.01, min: 0, max: 0.1, label: { key: m.chromaticPower } },
  ],
  helpers: [SAMPLE_SCREEN],
  main: `
void main() {
  vec2 dir = uv - vec2(0.5);
  float n = clamp(floor(sampleCount + 0.5), 1.0, 64.0);
  vec4 base = texture2D(screenTexture, uv);
  float r = 0.0;
  float b = 0.0;
  for (int i = 0; i < 64; i++) {
    float fi = float(i);
    if (fi >= n) break;
    vec2 off = dir * power * ((fi + 1.0) / n);
    r += sampleScreen(uv + off).r;
    b += sampleScreen(uv - off).b;
  }
  gl_FragColor = vec4(r / n, base.g, b / n, base.a);
}`,
});
